import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
/* ─── TOKENS ─────────────────────────────────────────────────────────── */
const T = {
  bg:      '#07080c',
  bg2:     '#0c0e14',
  bg3:     '#111520',
  surface: '#161b26',
  surf2:   '#1c2233',
  border:  'rgba(255,255,255,.06)',
  borderHi:'rgba(255,255,255,.11)',
  white:   '#dde5f5',
  muted:   'rgba(221,229,245,.42)',
  dim:     'rgba(221,229,245,.2)',
  blue:    '#3d8eff',
  cyan:    '#00c8f0',
  green:   '#2ecc71',
  red:     '#e74c3c',
  orange:  '#f39c12',
  purple:  '#9b59b6',
  sans:    '"Syne","DM Sans",system-ui,sans-serif',
  mono:    '"JetBrains Mono","Fira Code",monospace',
};

/* ─── SHARED ATOMS ───────────────────────────────────────────────────── */
function TabBar({ tabs, active, onChange }) {
  return (
    <div style={{ display:'flex', borderBottom:`1px solid ${T.border}`, marginBottom:0 }}>
      {tabs.map(t => (
        <button key={t.id} onClick={()=>onChange(t.id)} style={{
          padding:'12px 20px',
          border:'none',
          borderBottom:`2px solid ${active===t.id ? T.cyan : 'transparent'}`,
          background:'transparent',
          color: active===t.id ? T.white : T.muted,
          fontSize:13, fontWeight: active===t.id ? 700 : 400,
          fontFamily:T.sans, cursor:'pointer',
          display:'flex', alignItems:'center', gap:8,
          marginBottom:-1, transition:'color .15s',
          letterSpacing:'.01em',
        }}>
          <span style={{
            fontSize:16, filter: active===t.id ? 'none' : 'grayscale(1) opacity(.5)',
          }}>{t.icon}</span>
          {t.label}
        </button>
      ))}
    </div>
  );
}

function DropZone({ onFiles, accept, multiple=true, children, compact=false }) {
  const [drag, setDrag] = useState(false);
  const ref = useRef();

  const handle = (files) => {
    const arr = Array.from(files).filter(f =>
      accept ? accept.some(a => f.type.includes(a) || f.name.endsWith(a)) : true
    );
    if (arr.length) onFiles(arr);
  };

  return (
    <div
      onClick={() => ref.current.click()}
      onDragOver={e => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={e => { e.preventDefault(); setDrag(false); handle(e.dataTransfer.files); }}
      style={{
        border:`1.5px dashed ${drag ? T.cyan : T.borderHi}`,
        borderRadius:12,
        padding: compact ? '18px' : '36px 24px',
        textAlign:'center',
        cursor:'pointer',
        background: drag ? `rgba(0,200,240,.05)` : T.bg2,
        transition:'all .18s',
        userSelect:'none',
      }}
    >
      <input
        ref={ref} type="file"
        accept={accept?.join(',')}
        multiple={multiple}
        style={{ display:'none' }}
        onChange={e => handle(e.target.files)}
      />
      {children}
    </div>
  );
}

function Btn({ onClick, disabled, loading, children, variant='primary', small }) {
  const colors = {
    primary: { bg:T.blue+'22', border:T.blue+'55', color:T.blue },
    success: { bg:T.green+'18', border:T.green+'50', color:T.green },
    danger:  { bg:T.red+'14',  border:T.red+'45',   color:T.red  },
    ghost:   { bg:'transparent', border:T.border,  color:T.muted },
  };
  const c = colors[variant];
  return (
    <button onClick={onClick} disabled={disabled||loading} style={{
      padding: small ? '5px 12px' : '8px 18px',
      borderRadius:7, border:`1px solid ${c.border}`,
      background:c.bg, color:c.color,
      fontSize: small ? 11.5 : 13,
      fontWeight:600, fontFamily:T.sans,
      cursor: disabled||loading ? 'default':'pointer',
      opacity: disabled||loading ? .45 : 1,
      display:'inline-flex', alignItems:'center', gap:6,
      transition:'all .14s', whiteSpace:'nowrap',
    }}>
      {loading && <Spin size={11}/>}
      {children}
    </button>
  );
}

function Spin({ size=14 }) {
  return (
    <div style={{
      width:size, height:size, borderRadius:'50%',
      border:`2px solid rgba(255,255,255,.12)`,
      borderTopColor:'currentColor',
      animation:'spin .65s linear infinite', flexShrink:0,
    }}/>
  );
}

function Tag({ children, color=T.blue }) {
  return (
    <span style={{
      padding:'2px 7px', borderRadius:4,
      background:color+'18', border:`1px solid ${color}35`,
      color, fontSize:10.5, fontWeight:700,
      fontFamily:T.mono, letterSpacing:'.04em',
      textTransform:'uppercase',
    }}>{children}</span>
  );
}

function Progress({ value, label, color=T.cyan }) {
  return (
    <div style={{ marginTop:8 }}>
      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:5 }}>
        <span style={{ fontSize:11.5, color:T.muted }}>{label}</span>
        <span style={{ fontSize:11.5, color, fontFamily:T.mono }}>{Math.round(value)}%</span>
      </div>
      <div style={{ height:4, background:T.border, borderRadius:2, overflow:'hidden' }}>
        <div style={{
          height:'100%', width:`${value}%`,
          background:`linear-gradient(90deg,${color},${color}aa)`,
          borderRadius:2, transition:'width .3s ease',
        }}/>
      </div>
    </div>
  );
}

function FileRow({ file, meta, onRemove, extra }) {
  const kb = (file.size/1024).toFixed(1);
  const mb = (file.size/1024/1024).toFixed(2);
  const size = file.size > 1024*1024 ? `${mb} MB` : `${kb} KB`;
  return (
    <div style={{
      display:'flex', alignItems:'center', gap:10,
      padding:'8px 10px',
      background:T.bg2, border:`1px solid ${T.border}`,
      borderRadius:8,
    }}>
      <div style={{
        width:36, height:36, borderRadius:7, flexShrink:0,
        background:T.surface, border:`1px solid ${T.borderHi}`,
        display:'flex', alignItems:'center', justifyContent:'center',
        fontSize:18,
      }}>
        {meta?.preview
          ? <img src={meta.preview} alt="" style={{ width:34, height:34, objectFit:'cover', borderRadius:6 }}/>
          : meta?.icon || '📄'}
      </div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{
          fontSize:12.5, fontWeight:600, color:T.white,
          overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
        }}>{file.name}</div>
        <div style={{ fontSize:11, color:T.dim, marginTop:1 }}>{size}</div>
      </div>
      {extra}
      {onRemove && (
        <button onClick={onRemove} style={{
          width:22, height:22, borderRadius:5, border:`1px solid ${T.border}`,
          background:'transparent', color:T.muted, cursor:'pointer', flexShrink:0,
          fontSize:12, fontFamily:'inherit',
        }}>✕</button>
      )}
    </div>
  );
}

function SectionHead({ icon, title, subtitle }) {
  return (
    <div style={{ marginBottom:20 }}>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:4 }}>
        <span style={{ fontSize:22 }}>{icon}</span>
        <h2 style={{
          fontSize:18, fontWeight:800, color:T.white,
          fontFamily:T.sans, letterSpacing:'-.01em',
        }}>{title}</h2>
      </div>
      <p style={{ fontSize:13, color:T.muted, marginLeft:32 }}>{subtitle}</p>
    </div>
  );
}

/* ─── TOOL 1: IMAGE CONVERTER ────────────────────────────────────────── */
const IMG_FORMATS = [
  { id:'jpeg', label:'JPG', mime:'image/jpeg', ext:'jpg' },
  { id:'png',  label:'PNG', mime:'image/png',  ext:'png' },
  { id:'webp', label:'WebP',mime:'image/webp', ext:'webp'},
  { id:'bmp',  label:'BMP', mime:'image/bmp',  ext:'bmp' },
  { id:'gif',  label:'GIF', mime:'image/gif',  ext:'gif' },
];

function ImageConverter() {
  const [files,    setFiles]    = useState([]);
  const [targetFmt,setTargetFmt]= useState('webp');
  const [quality,  setQuality]  = useState(90);
  const [results,  setResults]  = useState([]);
  const [converting,setConverting]=useState(false);
  const [progress, setProgress] = useState(0);

  const addFiles = useCallback((newFiles) => {
    const processed = newFiles.map(f => ({
      file: f,
      id:   Math.random().toString(36).slice(2),
      preview: URL.createObjectURL(f),
      ext: f.name.split('.').pop().toLowerCase(),
    }));
    setFiles(prev => [...prev, ...processed]);
    setResults([]);
  }, []);

  const removeFile = (id) => {
    setFiles(prev => {
      const item = prev.find(f=>f.id===id);
      if (item) URL.revokeObjectURL(item.preview);
      return prev.filter(f=>f.id!==id);
    });
  };

  const convertAll = async () => {
    if (!files.length) return;
    setConverting(true);
    setResults([]);
    const fmt = IMG_FORMATS.find(f=>f.id===targetFmt);
    const out  = [];

    for (let i=0; i<files.length; i++) {
      const item = files[i];
      setProgress(Math.round((i/files.length)*100));

      try {
        const url = await convertImage(item.file, fmt.mime, quality/100);
        const origName = item.file.name.replace(/\.[^.]+$/, '');
        out.push({
          id:       item.id,
          name:     `${origName}.${fmt.ext}`,
          url,
          origSize: item.file.size,
          preview:  url,
        });
      } catch(e) {
        out.push({ id:item.id, name:item.file.name, error:e.message });
      }
    }

    setProgress(100);
    setResults(out);
    setConverting(false);
  };

  const convertImage = (file, targetMime, quality) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const src = URL.createObjectURL(file);
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width  = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        // White background for JPG (no transparency)
        if (targetMime === 'image/jpeg') {
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
        ctx.drawImage(img, 0, 0);
        URL.revokeObjectURL(src);
        const dataUrl = canvas.toDataURL(targetMime, quality);
        resolve(dataUrl);
      };
      img.onerror = () => { URL.revokeObjectURL(src); reject(new Error('Could not load image')); };
      img.src = src;
    });
  };

  const downloadAll = () => {
    results.filter(r=>!r.error).forEach(r => {
      const a = document.createElement('a');
      a.href = r.url;
      a.download = r.name;
      a.click();
    });
  };

  const downloadOne = (r) => {
    const a = document.createElement('a');
    a.href = r.url;
    a.download = r.name;
    a.click();
  };

  const fmt = IMG_FORMATS.find(f=>f.id===targetFmt);
  const hasJpg = targetFmt === 'jpeg';

  return (
    <div>
      <SectionHead
        icon="🖼️"
        title="Image Converter"
        subtitle="Convert between PNG, JPG, WebP, BMP, GIF — all in your browser, no upload"
      />

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
        {/* Left — Upload */}
        <div>
          <div style={{ fontSize:11, fontWeight:700, letterSpacing:'.08em', textTransform:'uppercase', color:T.dim, marginBottom:8 }}>
            Input Images
          </div>
          <DropZone
            onFiles={addFiles}
            accept={['image/']}
            multiple
          >
            <div style={{ fontSize:28, marginBottom:8 }}>🖼️</div>
            <div style={{ fontSize:13.5, fontWeight:600, color:T.white, marginBottom:4 }}>
              Drop images here or click to browse
            </div>
            <div style={{ fontSize:11.5, color:T.dim }}>
              PNG · JPG · WebP · BMP · GIF · SVG
            </div>
          </DropZone>

          {files.length > 0 && (
            <div style={{ marginTop:10, display:'flex', flexDirection:'column', gap:6 }}>
              {files.map(item => (
                <FileRow
                  key={item.id}
                  file={item.file}
                  meta={{ preview: item.preview }}
                  onRemove={() => removeFile(item.id)}
                  extra={<Tag color={T.cyan}>{item.ext.toUpperCase()}</Tag>}
                />
              ))}
            </div>
          )}
        </div>

        {/* Right — Settings */}
        <div>
          <div style={{ fontSize:11, fontWeight:700, letterSpacing:'.08em', textTransform:'uppercase', color:T.dim, marginBottom:8 }}>
            Output Format
          </div>

          {/* Format pills */}
          <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:14 }}>
            {IMG_FORMATS.map(f => (
              <button key={f.id} onClick={()=>setTargetFmt(f.id)} style={{
                padding:'7px 14px',
                borderRadius:7,
                border:`1px solid ${targetFmt===f.id ? T.cyan+'60' : T.border}`,
                background: targetFmt===f.id ? T.cyan+'16' : T.bg2,
                color: targetFmt===f.id ? T.cyan : T.muted,
                fontSize:12.5, fontWeight:700,
                fontFamily:T.mono, cursor:'pointer',
                transition:'all .13s',
              }}>{f.label}</button>
            ))}
          </div>

          {/* Quality slider — only for lossy formats */}
          {(targetFmt === 'jpeg' || targetFmt === 'webp') && (
            <div style={{ marginBottom:14 }}>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
                <span style={{ fontSize:11.5, color:T.muted }}>Quality</span>
                <span style={{ fontSize:12, color:T.cyan, fontFamily:T.mono, fontWeight:700 }}>{quality}%</span>
              </div>
              <input
                type="range" min={10} max={100} value={quality}
                onChange={e=>setQuality(Number(e.target.value))}
                style={{ width:'100%', accentColor:T.cyan }}
              />
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:10, color:T.dim, marginTop:2 }}>
                <span>Smaller file</span>
                <span>Better quality</span>
              </div>
            </div>
          )}

          {/* Format info */}
          <div style={{
            padding:'10px 12px', borderRadius:8,
            background:T.surface, border:`1px solid ${T.border}`,
            fontSize:12, color:T.muted, lineHeight:1.65, marginBottom:14,
          }}>
            {targetFmt==='jpeg' && '📷 JPG — Best for photos. Smaller files, lossy compression. No transparency.'}
            {targetFmt==='png'  && '🎨 PNG — Lossless quality. Supports transparency. Best for graphics/UI.'}
            {targetFmt==='webp' && '⚡ WebP — Modern format. Best compression with great quality. Transparent support.'}
            {targetFmt==='bmp'  && '🗂 BMP — Uncompressed bitmap. Large files, maximum compatibility.'}
            {targetFmt==='gif'  && '🎞 GIF — Limited to 256 colors. Good for simple graphics, supports animation.'}
          </div>

          {/* Convert button */}
          <Btn
            onClick={convertAll}
            disabled={!files.length}
            loading={converting}
            variant="primary"
          >
            Convert {files.length > 0 ? `${files.length} image${files.length>1?'s':''}` : 'images'} → {fmt?.label}
          </Btn>

          {converting && <Progress value={progress} label="Converting…"/>}
        </div>
      </div>

      {/* Results */}
      {results.length > 0 && (
        <div style={{ marginTop:20 }}>
          <div style={{
            display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10,
          }}>
            <div style={{ fontSize:13, fontWeight:700, color:T.white }}>
              ✅ {results.filter(r=>!r.error).length} converted
            </div>
            {results.filter(r=>!r.error).length > 1 && (
              <Btn onClick={downloadAll} variant="success" small>
                ⬇ Download All
              </Btn>
            )}
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
            {results.map(r => (
              <div key={r.id} style={{
                display:'flex', alignItems:'center', gap:10,
                padding:'8px 10px',
                background:T.bg2, border:`1px solid ${r.error ? T.red+'35' : T.green+'25'}`,
                borderRadius:8,
              }}>
                {r.error ? (
                  <>
                    <span style={{ fontSize:14 }}>❌</span>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:12.5, color:T.red }}>{r.name}</div>
                      <div style={{ fontSize:11, color:T.red+'90' }}>{r.error}</div>
                    </div>
                  </>
                ) : (
                  <>
                    <img src={r.preview} alt="" style={{
                      width:36, height:36, objectFit:'cover',
                      borderRadius:6, border:`1px solid ${T.borderHi}`,
                    }}/>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{
                        fontSize:12.5, fontWeight:600, color:T.white,
                        overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
                      }}>{r.name}</div>
                    </div>
                    <Tag color={T.green}>{fmt?.label}</Tag>
                    <Btn onClick={()=>downloadOne(r)} variant="success" small>
                      ⬇ Download
                    </Btn>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── TOOL 2: PDF MERGER ─────────────────────────────────────────────── */
function PDFMerger() {
  const [files,    setFiles]    = useState([]);
  const [merging,  setMerging]  = useState(false);
  const [result,   setResult]   = useState(null);
  const [error,    setError]    = useState('');
  const [progress, setProgress] = useState(0);
  const dragItem   = useRef(null);
  const dragOver   = useRef(null);

  const addFiles = useCallback((newFiles) => {
    const pdfs = newFiles.filter(f => f.type === 'application/pdf' || f.name.endsWith('.pdf'));
    setFiles(prev => [...prev, ...pdfs.map(f => ({
      file: f, id: Math.random().toString(36).slice(2),
    }))]);
    setResult(null); setError('');
  }, []);

  const removeFile = (id) => {
    setFiles(prev => prev.filter(f=>f.id!==id));
    setResult(null);
  };

  // Drag to reorder
  const onDragStart  = (i) => { dragItem.current = i; };
  const onDragEnter  = (i) => { dragOver.current = i; };
  const onDragEnd    = () => {
    const copy = [...files];
    const dragged = copy.splice(dragItem.current, 1)[0];
    copy.splice(dragOver.current, 0, dragged);
    dragItem.current = null; dragOver.current = null;
    setFiles(copy);
  };

  const merge = async () => {
    if (files.length < 2) { setError('Add at least 2 PDF files to merge.'); return; }
    setMerging(true); setError(''); setResult(null); setProgress(0);

    try {
      const formData = new FormData();
      files.forEach((item, i) => {
        formData.append(`file_${i}`, item.file);
        formData.append(`order_${i}`, i);
      });

      setProgress(20);

      const res = await fetch('http://localhost:8000/api/tools/pdf/merge/', {
        method: 'POST',
        body:   formData,
      });

      setProgress(80);

      if (!res.ok) {
        const err = await res.json().catch(()=>({error:'Merge failed'}));
        throw new Error(err.error || `Server error ${res.status}`);
      }

      const blob  = await res.blob();
      const url   = URL.createObjectURL(blob);
      const total = files.reduce((s,f)=>s+f.file.size,0);

      setProgress(100);
      setResult({
        url,
        name:     'merged.pdf',
        size:     blob.size,
        origSize: total,
        pages:    files.length,
      });
    } catch(e) {
      setError(e.message);
    } finally {
      setMerging(false);
    }
  };

  const download = () => {
    if (!result) return;
    const a = document.createElement('a');
    a.href = result.url;
    a.download = result.name;
    a.click();
  };

  const totalSize = files.reduce((s,f)=>s+f.file.size,0);
  const fmtSize   = (b) => b > 1024*1024 ? `${(b/1024/1024).toFixed(1)} MB` : `${(b/1024).toFixed(0)} KB`;

  return (
    <div>
      <SectionHead
        icon="📑"
        title="PDF Merger"
        subtitle="Combine multiple PDFs into one. Drag to reorder before merging."
      />

      <div style={{ display:'grid', gridTemplateColumns:'1fr 340px', gap:16 }}>
        {/* File list */}
        <div>
          <DropZone onFiles={addFiles} accept={['.pdf','application/pdf']} compact>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:10 }}>
              <span style={{ fontSize:22 }}>📄</span>
              <div style={{ textAlign:'left' }}>
                <div style={{ fontSize:13, fontWeight:600, color:T.white }}>Drop PDFs here or click to browse</div>
                <div style={{ fontSize:11, color:T.dim }}>Add multiple PDF files</div>
              </div>
            </div>
          </DropZone>

          {files.length > 0 && (
            <div style={{ marginTop:12 }}>
              <div style={{
                display:'flex', justifyContent:'space-between',
                alignItems:'center', marginBottom:8,
              }}>
                <span style={{ fontSize:11, color:T.dim }}>
                  {files.length} file{files.length>1?'s':''} · {fmtSize(totalSize)} total · drag to reorder
                </span>
                <Btn
                  onClick={()=>{ setFiles([]); setResult(null); setError(''); }}
                  variant="ghost" small
                >
                  Clear all
                </Btn>
              </div>

              <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
                {files.map((item, i) => (
                  <div
                    key={item.id}
                    draggable
                    onDragStart={() => onDragStart(i)}
                    onDragEnter={() => onDragEnter(i)}
                    onDragEnd={onDragEnd}
                    style={{
                      display:'flex', alignItems:'center', gap:8,
                      padding:'8px 10px',
                      background:T.bg2, border:`1px solid ${T.border}`,
                      borderRadius:8, cursor:'grab',
                    }}
                  >
                    {/* order badge */}
                    <div style={{
                      width:22, height:22, borderRadius:5, flexShrink:0,
                      background:T.surface, border:`1px solid ${T.borderHi}`,
                      display:'flex', alignItems:'center', justifyContent:'center',
                      fontSize:11, fontWeight:700, color:T.cyan, fontFamily:T.mono,
                    }}>{i+1}</div>
                    <span style={{ color:T.dim, fontSize:14, flexShrink:0 }}>⠿</span>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{
                        fontSize:12.5, fontWeight:600, color:T.white,
                        overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
                      }}>{item.file.name}</div>
                      <div style={{ fontSize:11, color:T.dim }}>{fmtSize(item.file.size)}</div>
                    </div>
                    <button onClick={()=>removeFile(item.id)} style={{
                      width:20, height:20, borderRadius:4, border:`1px solid ${T.border}`,
                      background:'transparent', color:T.muted, cursor:'pointer', fontSize:11,
                    }}>✕</button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Controls */}
        <div>
          <div style={{
            padding:'16px', background:T.bg2,
            border:`1px solid ${T.border}`, borderRadius:10,
            marginBottom:12,
          }}>
            <div style={{ fontSize:12, fontWeight:700, color:T.white, marginBottom:10 }}>
              Merge Summary
            </div>
            {[
              ['Files to merge', files.length || '—'],
              ['Total size',     files.length ? fmtSize(totalSize) : '—'],
            ].map(([label,val]) => (
              <div key={label} style={{
                display:'flex', justifyContent:'space-between',
                padding:'5px 0', borderBottom:`1px solid ${T.border}`,
                fontSize:12,
              }}>
                <span style={{ color:T.dim }}>{label}</span>
                <span style={{ color:T.white, fontFamily:T.mono }}>{val}</span>
              </div>
            ))}
            <div style={{ marginTop:12 }}>
              <Btn
                onClick={merge}
                disabled={files.length < 2}
                loading={merging}
                variant="primary"
              >
                Merge {files.length > 0 ? `${files.length} PDFs` : 'PDFs'}
              </Btn>
            </div>
            {merging && <Progress value={progress} label="Merging…" color={T.blue}/>}
          </div>

          {error && (
            <div style={{
              padding:'10px 12px', borderRadius:8,
              background:T.red+'12', border:`1px solid ${T.red}35`,
              fontSize:12, color:T.red,
            }}>{error}</div>
          )}

          {result && (
            <div style={{
              padding:'14px', background:T.green+'10',
              border:`1px solid ${T.green}35`, borderRadius:10,
            }}>
              <div style={{ fontSize:13, fontWeight:700, color:T.green, marginBottom:8 }}>
                ✅ Merged successfully!
              </div>
              {[
                ['Output size', fmtSize(result.size)],
                ['Input size',  fmtSize(result.origSize)],
                ['Saved',       `${Math.max(0,Math.round((1-result.size/result.origSize)*100))}%`],
              ].map(([label,val]) => (
                <div key={label} style={{
                  display:'flex', justifyContent:'space-between',
                  padding:'4px 0', fontSize:12,
                }}>
                  <span style={{ color:T.muted }}>{label}</span>
                  <span style={{ color:T.white, fontFamily:T.mono }}>{val}</span>
                </div>
              ))}
              <div style={{ marginTop:10 }}>
                <Btn onClick={download} variant="success">
                  ⬇ Download merged.pdf
                </Btn>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── TOOL 3: PDF MINIFIER ───────────────────────────────────────────── */
function PDFMinifier() {
  const [file,     setFile]     = useState(null);
  const [level,    setLevel]    = useState('medium');
  const [minifying,setMinifying]= useState(false);
  const [result,   setResult]   = useState(null);
  const [error,    setError]    = useState('');
  const [progress, setProgress] = useState(0);

  const LEVELS = [
    { id:'low',    label:'Light',    desc:'Minimal compression, fastest, best quality' },
    { id:'medium', label:'Balanced', desc:'Good compression, preserves most quality'   },
    { id:'high',   label:'Aggressive',desc:'Maximum compression, smallest file size'   },
  ];

  const addFile = useCallback((files) => {
    const pdf = files[0];
    setFile(pdf);
    setResult(null);
    setError('');
  }, []);

  const minify = async () => {
    if (!file) return;
    setMinifying(true); setError(''); setResult(null); setProgress(0);

    try {
      const formData = new FormData();
      formData.append('file',  file);
      formData.append('level', level);

      setProgress(15);

      const res = await fetch('http://localhost:8000/api/tools/pdf/minify/', {
        method: 'POST',
        body:   formData,
      });

      setProgress(80);

      if (!res.ok) {
        const err = await res.json().catch(()=>({error:'Compression failed'}));
        throw new Error(err.error || `Server error ${res.status}`);
      }

      const blob    = await res.blob();
      const url     = URL.createObjectURL(blob);
      const saved   = file.size - blob.size;
      const savedPct= Math.round((saved/file.size)*100);

      setProgress(100);
      setResult({
        url,
        name:     file.name.replace('.pdf', '_compressed.pdf'),
        origSize: file.size,
        newSize:  blob.size,
        saved,
        savedPct: Math.max(0, savedPct),
      });
    } catch(e) {
      setError(e.message);
    } finally {
      setMinifying(false);
    }
  };

  const download = () => {
    if (!result) return;
    const a = document.createElement('a');
    a.href = result.url;
    a.download = result.name;
    a.click();
  };

  const fmtSize = (b) => b > 1024*1024 ? `${(b/1024/1024).toFixed(2)} MB` : `${(b/1024).toFixed(0)} KB`;

  return (
    <div>
      <SectionHead
        icon="🗜️"
        title="PDF Compressor"
        subtitle="Reduce PDF file size while preserving readability. Powered by pikepdf on the server."
      />

      <div style={{ display:'grid', gridTemplateColumns:'1fr 340px', gap:16 }}>
        {/* Upload */}
        <div>
          <DropZone onFiles={addFile} accept={['.pdf','application/pdf']} multiple={false}>
            <div style={{ fontSize:32, marginBottom:8 }}>🗜️</div>
            <div style={{ fontSize:13.5, fontWeight:600, color:T.white, marginBottom:4 }}>
              Drop your PDF here or click to browse
            </div>
            <div style={{ fontSize:11.5, color:T.dim }}>One PDF at a time</div>
          </DropZone>

          {file && (
            <div style={{ marginTop:10 }}>
              <FileRow
                file={file}
                meta={{ icon:'📄' }}
                onRemove={()=>{ setFile(null); setResult(null); setError(''); }}
                extra={<Tag color={T.red}>PDF</Tag>}
              />
            </div>
          )}

          {/* Compression levels */}
          <div style={{ marginTop:16 }}>
            <div style={{
              fontSize:11, fontWeight:700, letterSpacing:'.08em',
              textTransform:'uppercase', color:T.dim, marginBottom:10,
            }}>
              Compression Level
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
              {LEVELS.map(l => (
                <button key={l.id} onClick={()=>setLevel(l.id)} style={{
                  padding:'10px 14px',
                  borderRadius:8,
                  border:`1px solid ${level===l.id ? T.cyan+'55' : T.border}`,
                  background: level===l.id ? T.cyan+'10' : T.bg2,
                  cursor:'pointer', textAlign:'left',
                  transition:'all .13s',
                  display:'flex', alignItems:'center', gap:10,
                }}>
                  <div style={{
                    width:10, height:10, borderRadius:'50%', flexShrink:0,
                    background: level===l.id ? T.cyan : T.border,
                    boxShadow: level===l.id ? `0 0 8px ${T.cyan}` : 'none',
                  }}/>
                  <div>
                    <div style={{ fontSize:12.5, fontWeight:600, color: level===l.id ? T.white : T.muted }}>
                      {l.label}
                    </div>
                    <div style={{ fontSize:11, color:T.dim, marginTop:1 }}>{l.desc}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Controls + Result */}
        <div>
          <div style={{
            padding:'16px', background:T.bg2,
            border:`1px solid ${T.border}`, borderRadius:10, marginBottom:12,
          }}>
            <div style={{ fontSize:12, fontWeight:700, color:T.white, marginBottom:10 }}>
              Compression Info
            </div>
            {[
              ['File',  file ? file.name.slice(0,24)+(file.name.length>24?'…':'') : '—'],
              ['Size',  file ? fmtSize(file.size) : '—'],
              ['Level', LEVELS.find(l=>l.id===level)?.label || '—'],
            ].map(([label,val]) => (
              <div key={label} style={{
                display:'flex', justifyContent:'space-between',
                padding:'5px 0', borderBottom:`1px solid ${T.border}`, fontSize:12,
              }}>
                <span style={{ color:T.dim }}>{label}</span>
                <span style={{ color:T.white, fontFamily:T.mono, overflow:'hidden', textOverflow:'ellipsis', maxWidth:160 }}>{val}</span>
              </div>
            ))}
            <div style={{ marginTop:12 }}>
              <Btn
                onClick={minify}
                disabled={!file}
                loading={minifying}
                variant="primary"
              >
                Compress PDF
              </Btn>
            </div>
            {minifying && <Progress value={progress} label="Compressing…" color={T.orange}/>}
          </div>

          {error && (
            <div style={{
              padding:'10px 12px', borderRadius:8,
              background:T.red+'12', border:`1px solid ${T.red}35`,
              fontSize:12, color:T.red, marginBottom:10,
            }}>{error}</div>
          )}

          {result && (
            <div style={{
              padding:'14px', background:T.green+'0d',
              border:`1px solid ${T.green}35`, borderRadius:10,
            }}>
              <div style={{ fontSize:13, fontWeight:700, color:T.green, marginBottom:10 }}>
                ✅ Compressed!
              </div>

              {/* Size comparison bar */}
              <div style={{ marginBottom:10 }}>
                <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:T.dim, marginBottom:4 }}>
                  <span>Before: {fmtSize(result.origSize)}</span>
                  <span>After: {fmtSize(result.newSize)}</span>
                </div>
                <div style={{ height:6, background:T.border, borderRadius:3, overflow:'hidden' }}>
                  <div style={{
                    height:'100%',
                    width:`${Math.max(5, 100-result.savedPct)}%`,
                    background:`linear-gradient(90deg,${T.green},${T.cyan})`,
                    borderRadius:3,
                  }}/>
                </div>
              </div>

              {[
                ['Original',  fmtSize(result.origSize)],
                ['Compressed',fmtSize(result.newSize)],
                ['Saved',     `${fmtSize(result.saved)} (${result.savedPct}%)`],
              ].map(([label,val]) => (
                <div key={label} style={{
                  display:'flex', justifyContent:'space-between',
                  padding:'4px 0', fontSize:12,
                }}>
                  <span style={{ color:T.muted }}>{label}</span>
                  <span style={{
                    color: label==='Saved' ? T.green : T.white,
                    fontFamily:T.mono, fontWeight: label==='Saved'?700:400,
                  }}>{val}</span>
                </div>
              ))}

              <div style={{ marginTop:10 }}>
                <Btn onClick={download} variant="success">
                  ⬇ Download compressed PDF
                </Btn>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── MAIN PAGE ──────────────────────────────────────────────────────── */
const TABS = [
  { id:'image', icon:'🖼️', label:'Image Converter' },
  { id:'merge', icon:'📑', label:'PDF Merger'      },
  { id:'minify',icon:'🗜️', label:'PDF Compressor'  },
];

export default function FileTools() {
      const navigate = useNavigate();
    
      const hostname = window.location.hostname;
    
    // app.dev.example.com => ["app", "dev", "example", "com"]
    const parts = hostname.split(".");
    
    const firstSubdomain =
      parts.length > 2 ? parts[0] : null;
    
    console.log(firstSubdomain);
    
    const path = window.location.pathname;
    
    // "/tools/useragent/"
    const match = path.match(/^\/tools\/files\/([^/]+)/);
    
    const toolName = match ? match[1] : null;
    
    console.log(toolName);
    
      const [active, setActive] = useState(toolName?toolName:firstSubdomain ? firstSubdomain : 'image');
      

  return (
    <>
      <style>{`
        @keyframes spin { to { transform:rotate(360deg); } }
        * { box-sizing:border-box; margin:0; padding:0; }
        input[type=range] { height:4px; cursor:pointer; }
        ::placeholder { color:rgba(221,229,245,.18) !important; }
      `}</style>
 <div className="page">
    <section className="section" style={{ paddingTop: 0 }}>
      <div style={{
        minHeight:'100vh',
        background:T.bg,
        padding:'28px 0 48px',
        fontFamily:T.sans,
      }}>

        {/* Header */}
        <div style={{ marginBottom:20 }}>
          <h1 style={{
            fontSize:'clamp(1.5rem,3vw,2rem)',
            fontWeight:800, color:T.white,
            letterSpacing:'-.025em', marginBottom:5,
            fontFamily:T.sans,
          }}>
            🗂️ File Tools
          </h1>
          <p style={{ fontSize:13, color:T.muted }}>
            Image conversion runs entirely in your browser · PDF tools processed securely on the server. And, no we don't store your files.
          </p>
        </div>

        {/* Tabs */}
        <TabBar tabs={TABS} active={active} onChange={(id) => {
          navigate(`/tools/files/${id}/`)
            setActive(id);
          }}/>

        {/* Tool panel */}
        <div style={{
          background:T.bg2,
          border:`1px solid ${T.border}`,
          borderTop:'none',
          borderRadius:'0 0 12px 12px',
          padding:'24px 22px',
        }}>
          {active === 'image'  && <ImageConverter/>}
          {active === 'merge'  && <PDFMerger/>}
          {active === 'minify' && <PDFMinifier/>}
        </div>
      </div>
      </section></div>
    </>
  );
}
